import { useSyncExternalStore } from 'react';
import type { BrandConfig } from './types';
import { d247 } from './configs/d247';
import { royalx } from './configs/royalx';

export type { BrandConfig } from './types';

const BRANDS: Record<string, BrandConfig> = { d247, royalx };
const DEFAULT_BRAND = 'd247';

function resolveBrand(): BrandConfig {
  const key = (import.meta.env.VITE_BRAND as string | undefined) ?? DEFAULT_BRAND;
  return BRANDS[key] ?? BRANDS[DEFAULT_BRAND] ?? d247;
}

/** The active brand for this build (VITE_BRAND=royalx npm run build). */
export const brand: BrandConfig = resolveBrand();

// --- Reactive brand logo ----------------------------------------------------
// Components render the logo from the build-time config, but the super admin may
// have uploaded a per-brand logo (brand.logoUrl from GET /brand). Keep it in a
// tiny external store so the logo updates the instant /brand resolves. Use
// `useBrandLogo()` instead of `brand.assets.logo` wherever the logo is shown.
let logoOverride: string | null = null;
const logoSubs = new Set<() => void>();

export function useBrandLogo(): string {
  return useSyncExternalStore(
    (cb) => {
      logoSubs.add(cb);
      return () => { logoSubs.delete(cb); };
    },
    () => logoOverride ?? brand.assets.logo,
  );
}

function setBrandLogo(url: string): void {
  if (!url || url === logoOverride) return;
  logoOverride = url;
  logoSubs.forEach((f) => f());
}

/**
 * Inject the active brand's primary color as CSS variables, and swap the
 * document title + favicons at runtime. Call once at startup before render.
 */
export function applyBrandTheme(config: BrandConfig = brand): void {
  const root = document.documentElement;
  const c = config.colors;

  root.style.setProperty('--primary', c.primaryHsl);
  root.style.setProperty('--ring', c.primaryHsl);
  root.style.setProperty('--brand-primary', c.primary);

  document.title = config.title;

  document
    .querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="shortcut icon"]')
    .forEach((link) => {
      link.href = config.assets.favicon;
    });

  const appleIcon = document.querySelector<HTMLLinkElement>(
    'link[rel="apple-touch-icon"]'
  );
  if (appleIcon) appleIcon.href = config.assets.appleTouchIcon;
}

/**
 * Runtime (per-domain) branding for the white-label / downline model.
 *
 * Instead of a build-time VITE_BRAND, the backend resolves the brand from the
 * request Host and returns it from `GET /brand`. We apply the static default
 * first (instant paint), then override from the API response when it arrives.
 *
 * The resolved brand is also stashed on `window.__BRAND__` so custom theme code
 * can read `theme` / `features` without re-fetching.
 */
export interface RemoteBrand {
  resolved: boolean;
  slug: string | null;
  brandName: string;
  title: string;
  primaryColor: string;
  primaryHsl: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  supportEmail: string | null;
  theme: unknown;
  features: unknown;
}

declare global {
  interface Window {
    __BRAND__?: RemoteBrand;
  }
}

export async function applyRemoteBrandTheme(): Promise<RemoteBrand | null> {
  // 1) Instant paint with the build-time default.
  applyBrandTheme();

  // 2) Fetch the Host-resolved brand and override.
  const apiBase = (import.meta.env.VITE_API_BASE_URL as string) || '/api';
  try {
    const res = await fetch(`${apiBase.replace(/\/$/, '')}/brand`, {
      credentials: 'include',
      headers: { 'X-Brand-Domain': window.location.hostname },
    });
    if (!res.ok) return null;
    const b = (await res.json()) as RemoteBrand;
    if (!b || !b.resolved) return b ?? null;

    const root = document.documentElement;
    if (b.primaryHsl) {
      root.style.setProperty('--primary', b.primaryHsl);
      root.style.setProperty('--ring', b.primaryHsl);
    }
    if (b.primaryColor) root.style.setProperty('--brand-primary', b.primaryColor);
    if (b.title) document.title = b.title;
    if (b.faviconUrl) {
      document
        .querySelectorAll<HTMLLinkElement>(
          'link[rel="icon"], link[rel="shortcut icon"]'
        )
        .forEach((link) => (link.href = b.faviconUrl as string));
    }
    if (b.logoUrl) setBrandLogo(b.logoUrl);

    window.__BRAND__ = b;
    return b;
  } catch {
    return null;
  }
}
